<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const reasons = computed(() => props.data?.reasons || []);
const totalLost = computed(() => props.data?.total_lost || 0);

const colors = [
  'bg-red-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-green-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-blue-500',
  'bg-indigo-500',
];

const getColor = index => colors[index % colors.length];

const maxCount = computed(() => {
  if (!reasons.value.length) return 1;
  return Math.max(...reasons.value.map(r => r.count || 0), 1);
});

const getBarWidth = count => {
  const percentage = (count / maxCount.value) * 100;
  return `${Math.max(percentage, 5)}%`;
};
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
        {{ t('DEAL_REPORTS.LOSS_REASONS.TITLE') }}
      </h3>
      <span class="text-sm text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.LOSS_REASONS.TOTAL') }}: {{ totalLost }}
      </span>
    </div>

    <div v-if="reasons.length" class="space-y-4">
      <div
        v-for="(reason, index) in reasons"
        :key="reason.reason_id || index"
        class="space-y-1"
      >
        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-700 dark:text-slate-300 truncate flex-1">
            {{ reason.reason }}
          </span>
          <span class="text-slate-500 dark:text-slate-400 ml-2">
            {{ reason.count }} ({{ reason.percentage }}%)
          </span>
        </div>
        <div
          class="h-6 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden"
        >
          <div
            class="h-full rounded-lg transition-all duration-300 flex items-center justify-end px-2"
            :class="getColor(index)"
            :style="{ width: getBarWidth(reason.count) }"
          >
            <span
              v-if="reason.percentage >= 15"
              class="text-xs text-white font-medium"
            >
              {{ reason.percentage }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="h-48 flex items-center justify-center">
      <div class="text-center">
        <span class="i-lucide-check-circle text-4xl text-green-500 mb-2" />
        <p class="text-slate-500 dark:text-slate-400">
          {{ t('DEAL_REPORTS.LOSS_REASONS.NO_LOSSES') }}
        </p>
      </div>
    </div>
  </div>
</template>
